/**
 * 전자서명 — 로컬 스토어 (Phase-1, mapper/BE 연결 전)
 * 설계: docs/agent-plans/CM/20260918-전자서명시스템-이식분석.md (es_template/es_field/es_request 초안)
 *       + docs/agent-plans/CM/20260923-AI문서아카이브-OCR전자서명계약인사문서-통합설계분석.md §4.2
 *
 * Phase 1 범위: 서명영역 좌표 편집기 대신, 문서 종류별로 필요한 "필드 목록"만 지정한다
 * (좌표 기반 편집기는 후속 — 이식분석 §7 리스크 참고). 외부 서명은 서명 링크(토큰) 발급까지만
 * 구현하고, 실제 발송(카카오·메일)·본인확인 수준은 BE 연동 시 결정한다(§4.2 남은 질문).
 */

const TPL_KEY_PREFIX = 'erp.archive.signature.template';
const REQ_KEY_PREFIX = 'erp.archive.signature.request';

export const SIGN_FIELD_TYPES = [
  { value: 'SIGNATURE', label: '서명' },
  { value: 'TEXT', label: '텍스트' },
  { value: 'DATE', label: '날짜' },
];

/** 문서 종류별 기본 필드 프리셋 — 계약서/납품확인서 등 문서마다 요구 필드가 다르다는 실사용 요구 반영 */
export const SIGNATURE_DOC_PRESETS = [
  { value: 'CONTRACT', label: '계약서' },
  { value: 'DELIVERY_CONFIRM', label: '납품확인서' },
  { value: 'INTEGRITY_PLEDGE', label: '청렴이행각서' },
  { value: 'SECURITY_PLEDGE', label: '보안서약서' },
  { value: 'ETC', label: '기타' },
];

export const REQUEST_STATUS = [
  { value: 'DRAFT', label: '작성중' },
  { value: 'SENT', label: '발송대기' },
  { value: 'VIEWED', label: '열람됨' },
  { value: 'SIGNED', label: '서명완료' },
  { value: 'EXPIRED', label: '만료' },
];

function templateKey(tenantCode) {
  return `${TPL_KEY_PREFIX}|${tenantCode || ''}`;
}
function requestKey(tenantCode) {
  return `${REQ_KEY_PREFIX}|${tenantCode || ''}`;
}

function readJson(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) ?? fallback;
  } catch {
    return fallback;
  }
}
function writeJson(key, value) {
  localStorage.setItem(key, JSON.stringify(value));
}

export function createUid(prefix = 'ES') {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export function emptyTemplate(partial = {}) {
  return {
    templateUid: '',
    templateName: '',
    docPreset: 'CONTRACT',
    status: 'draft', // draft | ready
    fields: [
      { fieldUid: createUid('EF'), label: '서명', type: 'SIGNATURE' },
    ],
    version: 1,
    ...partial,
  };
}

export function loadTemplates(tenantCode) {
  return readJson(templateKey(tenantCode), []);
}

export function saveTemplate(tenantCode, tpl) {
  const list = loadTemplates(tenantCode);
  const idx = list.findIndex((t) => t.templateUid === tpl.templateUid);
  const next = idx >= 0
    ? [...list.slice(0, idx), { ...tpl, version: (list[idx].version || 1) + 1 }, ...list.slice(idx + 1)]
    : [{ ...tpl, templateUid: tpl.templateUid || createUid('ET') }, ...list];
  writeJson(templateKey(tenantCode), next);
  return next;
}

export function removeTemplate(tenantCode, templateUid) {
  const next = loadTemplates(tenantCode).filter((t) => t.templateUid !== templateUid);
  writeJson(templateKey(tenantCode), next);
  return next;
}

export function loadRequests(tenantCode) {
  return readJson(requestKey(tenantCode), []);
}

export function saveRequest(tenantCode, req) {
  const list = loadRequests(tenantCode);
  const idx = list.findIndex((r) => r.requestUid === req.requestUid);
  const next = idx >= 0 ? [...list.slice(0, idx), req, ...list.slice(idx + 1)] : [req, ...list];
  writeJson(requestKey(tenantCode), next);
  return next;
}

/**
 * 외부 서명 링크 생성 — 원본 GAS 설계(Google OAuth)보다 가벼운 방식.
 * [[20260923-AI문서아카이브-OCR전자서명계약인사문서-통합설계분석]] §4.2:
 * 추측 불가능한 토큰 링크 + 접속 시 최소 본인확인(이름·연락처)을 전제로 한다.
 * 실제 발송(카카오 알림톡/메일)과 서명 페이지 자체는 BE 연동 후 구현 — 지금은 링크 문자열만 생성한다.
 */
export function buildSignLinkToken() {
  return createUid('SIGNLNK');
}

export function emptyRequest(templateUid, partial = {}) {
  return {
    requestUid: createUid('ER'),
    templateUid,
    counterpartyName: '',
    counterpartyContact: '',
    status: 'DRAFT',
    linkToken: '',
    createdAt: new Date().toISOString(),
    sentAt: '',
    signedAt: '',
    ...partial,
  };
}
